import { useCallback, useEffect, useRef, useState } from "react";
import { defaultFilters, NO_REPORT, REPORT_OPTIONS, REPORTS, reportParameters, validateReport } from "./qaReportConfig";
import { qaReportsApi } from "./qaReportsApi";
import { useQaReportHistory } from "./useQaReportHistory";
import type { QaReportFilters, ReportChoice, ReportError, ReportFormat, SelectOption } from "./qaReportTypes";

export function useQaReports(logout: () => void) {
  const [filters, setFilters] = useState<QaReportFilters>(defaultFilters);
  const [reportType, setReportType] = useState<ReportChoice>(NO_REPORT);
  const [error, setError] = useState<ReportError | null>(null);
  const [activeFormat, setActiveFormat] = useState<ReportFormat | null>(null);
  const loading = activeFormat !== null;
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [classes, setClasses] = useState<SelectOption[]>([]);
  const [suppliers, setSuppliers] = useState<SelectOption[]>([]);
  const generating = useRef(false);
  const mounted = useRef(true);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const history = useQaReportHistory(logout, setError);
  const loadRecentReports = history.loadRecentReports;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const initialize = useCallback(async () => {
    const loadOptions = async (
      endpoint: Parameters<typeof qaReportsApi.options>[0],
      setter: (options: SelectOption[]) => void,
      all: SelectOption,
    ) => {
      try {
        const response = await qaReportsApi.options(endpoint);
        if (!mounted.current) return;
        if (response.success) setter([all, ...response.data]);
        else if (response.errorStatus === 401) logoutRef.current();
        else setError({ title: `Could not load ${all.label.replace("All ", "")}` });
      } catch {
        if (mounted.current) setError({ title: "Could not load report filters", subtitle: "Check your connection and refresh the page." });
      }
    };
    await Promise.all([
      loadOptions("categoriesForDropdown", setCategories, { value: -1, label: "All categories" }),
      loadOptions("classesForDropdown", setClasses, { value: -1, label: "All classes" }),
      loadOptions("suppliersForDropdown", setSuppliers, { value: -1, label: "All suppliers" }),
      loadRecentReports(),
    ]);
  }, [loadRecentReports]);

  const printSelectedReport = async (format: ReportFormat = "pdf") => {
    if (generating.current) return;
    if (reportType.value === -1) {
      setError({ title: "Please select a report type" });
      return;
    }
    const validation = validateReport(reportType.value, filters, format);
    if (validation) { setError({ title: validation }); return; }
    const definition = REPORTS[reportType.value];
    const parameters = reportParameters(reportType.value, filters, format);
    generating.current = true;
    setActiveFormat(format);
    try {
      const response = await qaReportsApi.submit(definition.kind, parameters);
      if (!mounted.current) return;
      // Keep compatibility with servers that still expose synchronous PDFs.
      if (!response.success && response.errorStatus === 404 && format === "pdf") {
        const legacy = await qaReportsApi.generateLegacy(definition.endpoint, parameters);
        if (!mounted.current) return;
        if (legacy.success) window.open(legacy.data.location, "_blank");
        else {
          if (legacy.errorStatus === 401) logoutRef.current();
          setError({ title: legacy.data.message || "Could not submit the report" });
        }
        return;
      }
      if (!response.success) {
        if (response.errorStatus === 401) logoutRef.current();
        setError({ title: response.data.message || "Could not submit the report" });
        return;
      }
      history.upsertReport(response.data);
      await history.waitAndDownload(response.data);
    } catch (cause) {
      if (mounted.current) setError({
        title: "Could not generate the report",
        subtitle: cause instanceof Error ? cause.message : "Your filters are preserved. Check your connection and try again.",
      });
    } finally {
      generating.current = false;
      if (mounted.current) {
        setActiveFormat(null);
      }
    }
  };

  const resetFilters = () => setFilters(defaultFilters());
  const selectReport = (choice: ReportChoice) => { setReportType(choice); resetFilters(); };

  return {
    ...history,
    filters, setFilters, reportType, setReportType, selectReport, resetFilters,
    qaReportOptions: REPORT_OPTIONS, categories, classes, suppliers, initialize,
    printSelectedReport,
    error, clearError: () => setError(null), loading, activeFormat,
  };
}
